// Helpers for converting between UTC ISO strings and HTML datetime-local
// input values (wall-clock strings like "2026-04-14T06:45" with no offset),
// anchored to a specific site timezone rather than the browser's timezone.

export const utcIsoToSiteLocalDatetimeValue = (
  iso: string,
  timeZone: string
): string => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? "00";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}`;
};

export const siteLocalDatetimeValueToUtcIso = (
  local: string,
  timeZone: string
): string => {
  const [datePart, timePart] = local.split("T");
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h, mi] = timePart.split(":").map(Number);
  const guessUtcMs = Date.UTC(y, mo - 1, d, h, mi);
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const readInTz = (utcMs: number): number => {
    const parts = fmt.formatToParts(new Date(utcMs));
    const get = (type: string) =>
      parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);
    const hour = get("hour") === 24 ? 0 : get("hour");
    return Date.UTC(
      get("year"),
      get("month") - 1,
      get("day"),
      hour,
      get("minute"),
      get("second")
    );
  };
  // One-step offset correction; re-verify once to handle DST transition edges.
  const offset = readInTz(guessUtcMs) - guessUtcMs;
  let result = guessUtcMs - offset;
  const verifyOffset = readInTz(result) - result;
  if (verifyOffset !== offset) {
    result = guessUtcMs - verifyOffset;
  }
  return new Date(result).toISOString();
};
